/*!
 * © 2024. The EGT Universe. All Rights Reserved.
 * License: Requires ACTIVE AppArtisan Subscription.
 * For more information visit: https://egtuniverse.com/legal
 *
 * Created for Universe App Tools.
 */

/*
 * email.section — a TRANSPARENT grouping region with an optional eyebrow + title
 * and its own vertical rhythm. Where email.card is a visible surface, a section is
 * the invisible scaffolding that groups a run of blocks (or cards) and gives a
 * region a heading. It sits directly on the email body, so its title/eyebrow use
 * the brand-on-black colours (gold title, grey eyebrow) — never a colour that would
 * vanish on the true-black body.
 *
 * USAGE:
 *   { "namespace": "email.section", "eyebrow": "Your order", "title": "Receipt",
 *     "inner": [ { "namespace": "email.card", … }, … ] }
 *
 * Children render via uat/src/email/renderChildren.js against the server registry.
 */

var _ = require("../../../uam/module.js")(module.exports, {
    namespace: "email.section",
    title: "Email Section",
    description: "A transparent grouping region with an optional eyebrow + title and vertical rhythm, holding child email blocks or cards.",
    supportServer: true,
    control: false,
    foruseIn: ["email"],
    fields: {
        "eyebrow": {
            title: "Eyebrow",
            type: "string",
            description: "Optional small uppercase label above the title.",
            required: false
        },
        "title": {
            title: "Title",
            type: "string",
            description: "Optional section heading (rendered in the brand accent colour).",
            required: false
        },
        "padding": {
            title: "Padding",
            type: "string",
            description: "CSS padding for the region (1–4 space-separated lengths). Default '12px 20px'.",
            required: false
        },
        "align": {
            title: "Align",
            type: "string",
            description: "Horizontal alignment: left | center | right. Default left.",
            required: false,
            default: "left"
        },
        "inner": {
            title: "Blocks",
            type: "inner",
            description: "The child email.* blocks or cards this section groups.",
            required: false
        }
    }
});

var $ = require("../../../uam/scafolding.js");

// Escape a string for safe insertion as HTML text content.
function escText(s) {
    return String(s == null ? "" : s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}

function resolvePadding(value, fallback) {
    if (typeof value === "string") {
        var v = value.trim();
        if (/^(\d+(\.\d+)?(px|rem|em|%)\s*){1,4}$/.test(v + " ")) { return v; }
    }
    return fallback;
}

function resolveAlign(value) {
    var v = (typeof value === "string") ? value.trim().toLowerCase() : "";
    return (v === "center" || v === "right") ? v : "left";
}

exports.renderEmail = async function (options) {
    options = options || {};

    // Lazy requires — see email.card.js for the circular-dependency reasoning.
    var registry = require("../registry.app.server.js");
    var renderChildren = require("../email/renderChildren.js").renderChildren;

    var cfg = $.config || {};
    var app = (cfg.my && cfg.my.app) ? cfg.my.app : {};
    var colors = app.colors || {};
    var fonts = app.fonts || {};
    // Title colour: accent -> primary -> NEUTRAL grey (NEVER gold). EGT sets
    // primary=#C48E2F so its title is unchanged; a brand-less tenant gets #6C757D.
    var primary = colors.accent || colors.primary || "#6C757D";
    // Body font: config-driven, neutral Arial fallback (NEVER a serif default).
    var bodyFont = fonts.body || "Arial,Helvetica,sans-serif";

    var kids = options.inner || options.i || options.content || [];
    var rendered = await renderChildren(registry, kids);

    var padding = resolvePadding(options.padding, "12px 20px");
    var align = resolveAlign(options.align);

    var head = "";
    var textHead = [];
    if (typeof options.eyebrow === "string" && options.eyebrow.trim()) {
        head += `<p style="margin:0 0 4px 0;padding:0;font-family:${bodyFont};font-size:12px;letter-spacing:1px;text-transform:uppercase;color:#8C8C8C;">${escText(options.eyebrow.trim())}</p>`;
        textHead.push(options.eyebrow.trim().toUpperCase());
    }
    if (typeof options.title === "string" && options.title.trim()) {
        head += `<h2 style="margin:0 0 10px 0;padding:0;font-family:${bodyFont};font-size:20px;font-weight:bold;color:${primary};line-height:1.3;">${escText(options.title.trim())}</h2>`;
        textHead.push(options.title.trim());
    }

    var html = `<tr>
  <td align="${align}" style="padding:${padding};font-family:${bodyFont};">
    ${head}
    <table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" style="width:100%;">
      ${rendered.html}
    </table>
  </td>
</tr>`;

    var textParts = [];
    if (textHead.length) { textParts.push(textHead.join("\n")); }
    if (rendered.text) { textParts.push(rendered.text); }

    return { html: html, text: textParts.join("\n") };
};

exports.render = async function (options) {
    var { html } = await exports.renderEmail(options);
    return [{
        n: "ua.element.serverNotSupported",
        inner: {
            n: "json",
            type: "ua/interface",
            by: "email.section",
            i: JSON.stringify({
                n: "html",
                html: `<div style="max-width:480px;margin:0 auto;font-family:Arial,Helvetica,sans-serif;">${html}</div>`
            })
        }
    }];
};
